/**
 * useProofBundle — fetches the full ProofBundle for a vehicle via
 * GET /v1/vehicles/:id/proof.
 *
 * Uses TanStack Query (server-state that isn't Firestore). The bundle is
 * large and changes only when a new ledger event is appended, so a 60-second
 * staleTime keeps network chatter low.
 *
 * Online-only: the trust API requires a network connection. When offline
 * the query returns the last cached result (if any) so the panel can still
 * show previously verified data; the "no bundle" state (null) renders a
 * benign "no provenance record" message rather than an error.
 *
 * Returns null (not an error) when the server responds 404 — meaning no
 * ledger exists yet for this vehicle, which is normal for newly added cars.
 */

import { ApiError, apiFetch } from '@/lib/api';
import type { ProofBundle } from '@/types/domain';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** TanStack Query key factory for proof bundles. */
export const proofBundleKeys = {
  forVehicle: (vehicleId: string) => ['proofBundle', vehicleId] as const,
};

export interface UseProofBundleResult {
  bundle: ProofBundle | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fetch the current ProofBundle for a vehicle.
 *
 * @param vehicleId - The vehicle's Firestore id. Pass null/undefined to skip fetching.
 */
export function useProofBundle(vehicleId: string | null | undefined): UseProofBundleResult {
  const result: UseQueryResult<ProofBundle | null, Error> = useQuery<ProofBundle | null, Error>({
    queryKey: proofBundleKeys.forVehicle(vehicleId ?? ''),
    queryFn: async () => {
      if (!vehicleId) return null;
      try {
        return await apiFetch<ProofBundle>(`/v1/vehicles/${vehicleId}/proof`);
      } catch (err) {
        // 404 = no ledger yet — not an error condition, return null.
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: Boolean(vehicleId),
    staleTime: 60_000, // 60 s — bundles change only on new ledger events
    // Don't retry automatically — the ProofBundle is a dogfood trust surface,
    // not a critical path. User can refresh. Retry on 5xx would also delay the
    // error state, which is worse UX than a fast "no provenance yet" panel.
    retry: false,
  });

  return {
    bundle: result.data ?? null,
    loading: result.isLoading,
    error: result.error ?? null,
  };
}
